import { Everything } from '@hit/amorphic-types';
import { amorphicStatic, property, supertypeClass } from '@haventech/amorphic';
import rp from 'request-promise-native';
import { AgentDefaultCommissionSplit } from '../agent/AgentDefaultCommissionSplit';
import { HavenStats } from '../havenStats/HavenStats';
import { Utils } from '../Utils';
import { Address } from './Address';
import { AgentProfile, PrincipalType } from './AgentProfile';
import { Party } from './Party';
import { Person } from './Person';
import { Phone } from './Phone';
import { Policy } from './Policy';
import { PolicyAgent } from './PolicyAgent';
import { ExamVendor } from '../interfaces/CommonTypes';
import { Designation } from '../../../coverpath-agent/public/js/types/Types';
import { serverOnly } from '../helpers';
import { CommonUtils } from '../static/CommonUtils';
import { CdmService } from '../services/CdmService';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../logger/UILoggerSingleton';

export enum Agent_Role {
  BROKERAGE_DIRECTOR = 'BrokerageDirector',
  SALES_MANAGER = 'SalesManager',
  PROXY = 'Delegation',
}

export type CDMProducerResponse = {
  producerId: string;
  statusCode: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  businessEmail: string;
  agencyName?: string;
  businessPhone?: string;
  businessUnit: BusinessUnit;
  lastFourOfSSN: number;
  nickName: string;
  businessAddressLine1: string;
  businessAddressLine2?: string;
  businessAddressLine3?: string;
  businessCity: string;
  businessState: string;
  businessZip: string;
  businessCountry?: string;
  agencyId: string; //(canonical BPID for MMFA for preferred upline agency)
  corpId: string; // populated only for subproducers
  producerAltCategory: string;
  specialCare: boolean;
  blueChipCircle: boolean;
  contractCode?: string;
};

export enum BusinessUnit {
  MmfaCareer = 'MmfaCareer',
  ThirdParty = 'ThirdParty',
  Manual = 'Manual',
  MmfaBrokerage = 'MmfaBrokerage',
}

export enum ContractCode {
  GA = 'Z212',
}

const NWM_REPLACEMENT = 'NWM';

@supertypeClass({ toClient: true, toServer: true })
export class Agent extends Everything {
  static readonly proxyLoginIdRegex: RegExp = /^(AE\d{5})\+(AA\d{6})(,AA\d{6})*$/;
  static readonly dualRoleLoginIdRegex: RegExp = /^(AA\d{6})\+(AA\d{6})(,AA\d{6})*$/;

  @property({ toServer: false, comment: 'Indicates the producerId mocked for local use' })
  mockedProducerId?: string = '';

  @serverOnly()
  static async getOrCreate(
    producerId: string,
    agencyId: string,
    firstName: string,
    lastName: string,
    email: string,
    businessUnit: BusinessUnit,
    groups: string[],
    displayName: string,
    session
  ): Promise<{ agent: Agent; firstLogin: boolean }> {
    const mockDualRoleLogin = Agent.dualRoleLoginIdRegex.test(producerId);
    let mockedProducerId = '';

    const nonProdMode = !Utils.isProdEnv();

    if (mockDualRoleLogin) {
      mockedProducerId = producerId;
      producerId = producerId.match(Agent.dualRoleLoginIdRegex)[1];
    }

    let requestTime = process.hrtime();
    let timeDiffInMilliseconds: number;

    LoggerSingleton.getInstance().info(
      {
        module: 'Agent',
        function: 'getOrCreate',
        category: Category.request,

        data: {
          producerId: producerId,
        },
      },
      'Loading Agent...'
    );

    const agents = await Agent.persistorFetchByQuery({ producerId: producerId }, { session: session });
    const txn = amorphicStatic.beginDefaultTransaction();

    let agent: Agent = agents[0];
    if (!agent) {
      agent = this.createNewAgent(txn, producerId, firstName, lastName, email, businessUnit, displayName, nonProdMode);
    }

    timeDiffInMilliseconds = Utils.getTimeOffset(requestTime);
    HavenStats.singletonStatsDClient().timing(
      'Agent.getOrCreate.getOrCreateAgent.request.time',
      timeDiffInMilliseconds,
      { unit: 'milliseconds' }
    );

    requestTime = process.hrtime();

    // Get demographics by hitting cdm-producers.
    const inCdm = await agent.updateAgentFromCDM(txn, mockDualRoleLogin);

    timeDiffInMilliseconds = Utils.getTimeOffset(requestTime);
    HavenStats.singletonStatsDClient().timing(
      'Agent.getOrCreate.updateContactInfoFromCDMAndUpdateLicensesFromCDM.request.time',
      timeDiffInMilliseconds,
      { unit: 'milliseconds' }
    );

    let firstLogin = false;
    // If agent not in Hit database, not in CDM, and not Mock Dual Role Login we cannot let the user login
    if (!agents[0] && !inCdm && !mockDualRoleLogin) {
      return { agent: undefined, firstLogin };
    }

    if (!agent.profile) {
      agent.profile = AgentProfile.new(agent, PrincipalType.Agent);
    }

    // Update from OKTA incase the previous call failed or did not return the data we need
    agent.updateAgentInfoFromOKTA(txn, inCdm, firstName, lastName, email, agencyId, businessUnit, displayName);
    const hasAgentLoggedIn: boolean = agent.hasEverLoggedIn;
    if (!hasAgentLoggedIn) {
      agent.hasEverLoggedIn = true;
      firstLogin = true;
    }

    if (mockDualRoleLogin) {
      agent.mockedProducerId = mockedProducerId;
    } else if (!mockDualRoleLogin && Utils.isTestingEnv()) {
      // reset the mockedProducerId property if not mocklogin
      agent.mockedProducerId = '';
    }

    agent.groups = groups;
    agent.setDirty(txn);
    await amorphicStatic.end(txn);

    return { agent, firstLogin };
  }

  public async updateAgentFromCDM(txn, mockDualRoleLogin: boolean) {
    const inCdm = await this.updateContactInfoFromCDM(txn);
    return inCdm;
  }

  private static createNewAgent(
    txn,
    producerId: string,
    firstName: string,
    lastName: string,
    email: string,
    businessUnit?: BusinessUnit,
    displayName?: string,
    nonProdMode?: boolean
  ): Agent {
    const agent: Agent = Agent.new(producerId);
    agent.person.firstName = firstName;
    agent.person.lastName = lastName;
    agent.person.email = email;
    agent.genericLink = agent.generateGenericLink();

    if (!!businessUnit) {
      agent.businessUnit = businessUnit;
    }
    if (nonProdMode && displayName) {
      agent.profile?.setDisplayName(displayName);
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'Agent',
        function: 'getOrCreate',
        category: UICategory.request,

        data: {
          producerId: producerId,
        },
      },
      'No agent found, creating a new agent.'
    );

    agent.setDirty(txn, true, true);
    return agent;
  }

  /* The agency id from CDM. */
  @property({ toServer: false, rule: ['text', 'required'], comment: 'Serving agency Id number' })
  agencyId: string;

  /* The agency name from CDM. */
  @property({ toServer: false, rule: ['text', 'required'], comment: 'Serving agency Name' })
  agencyName: string;

  /* The corporation id from CDM. */
  @property({
    toServer: false,
    rule: ['text'],
    comment: 'Corporation Id. Populated only for subproducers(producers whose direct parent is corp instead of agency)',
  })
  corpId: string;

  /* The agent id from okta.*/
  @property({ toServer: false, rule: ['text', 'required'], comment: 'Serving agent(producer) Id number' })
  producerId: string;

  //todo: HL-72669 - no longer used, remove references to it in hitDataService and integration tests
  @property({ comment: 'Can the agent sell in the state' })
  licenseStatus = {};

  /* Personal info for the agent.*/
  @property({ comment: 'Personal info for the agent', getType: () => Party })
  party: Party;

  /* Generic link T4C*/
  @property({ toServer: false, comment: 'Link used to submit policies, with no quote info' })
  genericLink: string;

  @property({ comment: 'true if the producer logged in at least once' })
  hasEverLoggedIn: boolean;

  @property({ getType: () => AgentProfile })
  profile: AgentProfile;

  @property({
    toServer: false,
    comment: 'High level functional grouping of advisor types - MmfaCareer, ThirdParty, Manual, MmfaBrokerage',
  })
  businessUnit: BusinessUnit = BusinessUnit.Manual;

  @property({ getType: () => AgentDefaultCommissionSplit })
  agentDefaultCommissionSplits: Array<AgentDefaultCommissionSplit> = [];

  /**
   * @deprecated - Use groups instead
   */
  @property({ toServer: false, comment: 'The data.group from SAML this Agent is part of' })
  group: string;

  // This will capture all groups the logged in user belongs to. The above group column is deprecated and
  // will be removed next sprint
  @property({
    toServer: false,
    type: Array,
    getType: () => String,
    comment:
      'The data.groups from SAML this Agent is part of. This is an array of strings like fim-coverpath-agent, fim-coverpath-help etc',
  })
  groups: string[];

  @property({
    toServer: false,
    comment:
      'The role(s) of Agent - Valid values are BrokerageDirector, SalesManager, Delegation. We donot use this field any more',
    getType: () => {
      return Agent_Role;
    },
  })
  roles: Agent_Role[];

  @property({
    persist: false,
    comment: 'This lists any designations an Agent may have including Society 1851, Special Care, etc.',
    getType: (): Array<Designation> => {
      return [];
    },
  })
  designations: Array<Designation>;

  /**
   * This property name is very generic because we are not sure entire subset of properties
   * that CDM might return which is receiving from Callidus.
   * And keeping the name consistent with CDM
   */
  @property({
    toServer: false,
    comment: 'Employment History category - to be used for NWM replacement watch',
  })
  producerAltCategory: string;

  @property({ comment: 'false if the producer is no longer active' })
  isActive: boolean = true;

  /* The merged agency ids from CDM. This is for dual-role user only. */
  @property({ toServer: false, type: Array, persist: false, comment: 'Merged Agency Ids', getType: () => String })
  mergedAgencyIds: Array<string> = [];
  constructor() {
    super();
  }

  static new(producerId?: string) {
    return new Agent().init(producerId);
  }

  init(producerId?: string) {
    this.producerId = producerId || '';
    this.person = Person.new();
    this.address = Address.new();
    this.phone = Phone.new();
    this.profile = AgentProfile.new(this, PrincipalType.Agent);
    return this;
  }

  get person(): Person {
    return <Person>this.party;
  }

  set person(person) {
    this.party = person;
  }

  get address(): Address {
    return this.party.businessAddress;
  }

  set address(address) {
    this.party.businessAddress = address;
  }

  get phone(): Phone {
    return this.party.phone;
  }

  set phone(phone: Phone) {
    this.party.phone = phone;
  }

  get producerIdTrimmed() {
    if (this.producerId) {
      return this.producerId.match(/\d+/)[0];
    }

    return null;
  }

  // Agency ids are 6 digits but they are padded to the left with 4 leading 0.
  // This function trims the agency id and only returns the last 6 'digits' of it.
  get agencyIdTrimmed() {
    if (this.agencyId) {
      return this.agencyId.slice(-6);
    }
    return null;
  }

  get displayNameWithLastName(): string {
    return `${this.displayName} ${this.person.lastName}`.trim();
  }

  get displayName(): string {
    return (this.profile && this.profile.displayName) || this.person.firstName;
  }

  getExamVendor(): string {
    return this.profile && this.profile.examVendor ? this.profile.examVendor : ExamVendor.ExamOne;
  }

  /**
   * Helper to return this agent as a PolicyAgent
   * @param {Policy} policy - The policy this agent will be attached to.
   * @returns {PolicyAgent} instance.
   */
  asPolicyAgent(policy?: Policy): PolicyAgent {
    return PolicyAgent.create(policy, this);
  }

  toString(): string {
    return this.person.getFullName() + ' ' + this.agencyId;
  }

  /**
   * Generate the generic link an agent can post for self referral.
   * @returns {string}
   */
  generateGenericLink(): string {
    const referralSnippet = [this.person.firstName, this.person.lastName, this.producerIdTrimmed]
      .join('-')
      .replace(/\s/g, '-');

    const clientUrl = this.amorphic.config.nconf.get('coverpathClientBaseUrl');

    return `${clientUrl}/?fg=${referralSnippet}`;
  }

  /**
   * use our SSO login creds as our source of truth for email and firstname, lastname
   * in an agent record
   */
  updateAgentInfoFromOKTA(
    txn,
    inCdm: boolean,
    firstName?: string,
    lastName?: string,
    email?: string,
    agencyId?: string,
    businessUnit?: BusinessUnit,
    displayName?: string
  ) {
    this.person.firstName = firstName || this.person.firstName;
    this.person.lastName = lastName || this.person.lastName;
    this.person.email = email || this.person.email;
    this.person.setDirty(txn);

    if (displayName) {
      this.profile.setDisplayName(displayName);
      this.profile.setDirty(txn);
    }

    // if we're not getting person info from cdm-producers, use info from SSO.
    if (!inCdm) {
      // We will not get this from OKTA but only mocklogin. Soon this code should be removed.
      if (agencyId) {
        this.agencyId = agencyId;
      }
      if (businessUnit) {
        this.businessUnit = businessUnit;
      }

      if (!displayName) {
        this.profile.setDisplayName(`${this.person.firstName}`);
      }

      this.setDirty(txn);
    }
  }

  @serverOnly()
  async updateContactInfoFromCDM(txn): Promise<boolean> {
    const requestTime = process.hrtime();

    try {
      const cdmProducerResponse = await this.getProducerInfoFromCDM(this.producerId);
      this.updateAgentAddress(cdmProducerResponse);
      this.address.setDirty(txn);
      this.updateAgentPhone(cdmProducerResponse);
      this.phone.setDirty(txn);

      this.businessUnit = cdmProducerResponse.businessUnit;
      this.agencyId = cdmProducerResponse.agencyId;
      this.corpId = cdmProducerResponse.corpId ? cdmProducerResponse.corpId : null;
      this.agencyName = cdmProducerResponse.agencyName;
      this.producerAltCategory = cdmProducerResponse.producerAltCategory;

      if (!this.person.email) {
        this.useBrokerBusinessEmail(cdmProducerResponse, txn);
      }

      await this.updateAgentDesignations(cdmProducerResponse);

      this.setDirty(txn);
      const timeDiffInMilliseconds = Utils.getTimeOffset(requestTime);
      HavenStats.singletonStatsDClient().timing(
        'Agent.getOrCreate.updateContactInfoFromCDM.request.time',
        timeDiffInMilliseconds,
        { unit: 'milliseconds' }
      );

      return true;
    } catch (err) {
      // Eat up the error because not being able to get contact info is
      // non fatal and the agent can continue using the app to some extent
      LoggerSingleton.getInstance().warn(
        {
          module: 'Agent',
          function: 'updateContactInfoFromCDM',

          category: Category.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              producerId: this.producerId,
            },
          },
        },
        'Failed to update agent info with latest data from CDM.'
      );

      return false;
    }
  }

  //todo: HL-33822, move into CDMService
  @serverOnly()
  async getProducerInfoFromCDM(producerId: string): Promise<CDMProducerResponse> {
    let demographicsResponse;
    const cdmUrl: string = this.amorphic.config.nconf.get('cdmProducersUrl');
    const asOfDate = new Date().toISOString();

    try {
      // Demographics info
      const demographicRequestUrl: string = `${cdmUrl}/producers/${producerId}?asOfDate=${asOfDate}`;
      const demographicOptions = {
        uri: demographicRequestUrl,
        json: true,
        headers: {
          ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
        },
      };

      Utils.attachBearerTokenForDevOnlyRoutes(demographicOptions);
      demographicsResponse = await rp(demographicOptions);
      Agent.logCDMResponse(demographicsResponse, 'Agent', 'getProducerInfoFromCDM');
    } catch (err) {
      throw new Error(`Failed to get latest agent info from CDM: ${err.message}`);
    }

    return demographicsResponse;
  }

  //todo: HL-33822, move into CDMService
  @serverOnly()
  async validateSecuredDateCdm(producerId: string, securedDate): Promise<CDMProducerResponse> {
    let demographicsResponse;
    const cdmUrl: string = this.amorphic.config.nconf.get('cdmProducersUrl');
    try {
      // Demographics info
      const demographicRequestUrl: string = `${cdmUrl}/producers/${producerId}?asOfDate=${securedDate}`;
      const demographicOptions = {
        uri: demographicRequestUrl,
        json: true,
        headers: {
          ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
        },
      };
      Utils.attachBearerTokenForDevOnlyRoutes(demographicOptions);
      demographicsResponse = await rp(demographicOptions);
      Agent.logCDMResponse(demographicsResponse, 'Agent', 'getProducerInfoFromCDM');
    } catch (err) {
      throw {
        type: 'invalidForDate',
        message: 'Agent is not active for policy secured date',
        rootCause: err,
      };
    }
    return demographicsResponse;
  }

  //We don't use this function any more
  @serverOnly()
  async getGroupsFromCDM(): Promise<Agent_Role[]> {
    let groupResponse;
    const cdmUrl: string = this.amorphic.config.nconf.get('cdmProducersUrl');

    try {
      // Group/Role info
      const groupRequestUrl: string = `${cdmUrl}/users/${this.producerId}/group_types`;
      const groupOptions = {
        uri: groupRequestUrl,
        json: true,
        headers: {
          ...LoggerSingleton.getInstance()?.getHeaderContextFromLocalStorage?.(),
        },
      };

      Utils.attachBearerTokenForDevOnlyRoutes(groupRequestUrl);
      groupResponse = await rp(groupOptions);
      Agent.logCDMResponse(groupResponse, 'Agent', 'getGroupTypeProducerInfoFromCDM');
    } catch (err) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Agent',
          function: 'getProducerInfoFromCDM',
          category: Category.security,

          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
          },
        },
        'Error in call to cdm groups endpoint'
      );
    }

    return groupResponse;
  }

  private updateAgentAddress(response: CDMProducerResponse) {
    this.address.street = response.businessAddressLine1;
    this.address.line1 = response.businessAddressLine2;
    this.address.line2 = response.businessAddressLine3;
    this.address.city = response.businessCity;
    this.address.zip = response.businessZip;
    this.address.state = response.businessState;
    if (!!response.businessCountry) {
      this.address.country = response.businessCountry;
    }
  }

  @serverOnly()
  private async updateAgentDesignations(response: CDMProducerResponse) {
    this.designations = Utils.convertCdmDesignationsArrayToStringArray([
      { blueChipCircle: response.blueChipCircle },
      { specialCare: response.specialCare },
    ]);
  }

  private updateAgentPhone(response) {
    if (response.businessPhone && !this.profile.isPhoneNumberUpdatedByAgent) {
      this.phone.number = CommonUtils.parsePhoneNumber(response.businessPhone);
      this.phone.type = Phone.TYPES.work;
      return;
    }
  }

  /**
   * We use business email for brokers because they may not have option to provide desired email during onboarding process
   * Refer to HL-27278 for more info
   */
  private useBrokerBusinessEmail(cdmProducerResponse: CDMProducerResponse, txn) {
    const businessUnit = cdmProducerResponse.businessUnit;
    const businessEmail = cdmProducerResponse.businessEmail;

    if (businessUnit === BusinessUnit.MmfaBrokerage && businessEmail) {
      this.person.email = businessEmail;
      this.person.setDirty(txn);
    }
  }

  public get isThirdPartyProducer(): boolean {
    return this.businessUnit === BusinessUnit.ThirdParty;
  }

  public get fullName(): string {
    return this.person.getFullName();
  }

  private static logCDMResponse(response: CDMProducerResponse, moduleName: string, activityName: string) {
    UILoggerSingleton.getInstance().info(
      {
        module: moduleName,
        function: activityName,
        category: UICategory.request,
        data: {
          producerId: response.producerId,
          statusCode: response.statusCode,
          agencyName: response.agencyName,
          businessUnit: response.businessUnit,
          nickName: response.nickName,
          agencyId: response.agencyId,
          corpId: response.corpId,
          blueChipCircle: response.blueChipCircle,
          businessState: response.businessState,
        },
      },
      'CDM Response'
    );
  }

  onWatchForNWMReplacement() {
    return this.producerAltCategory === NWM_REPLACEMENT;
  }

  public async updateMergedAgencyIds(): Promise<void> {
    try {
      if (this.agencyId) {
        const agencyMergers = await new CdmService().getAgencyMergersFromCdm([this.agencyId]);

        if (agencyMergers.length === 1) {
          this.mergedAgencyIds = agencyMergers[0];
        }
      }
    } catch (err) {
      UILoggerSingleton.getInstance().warn(
        {
          module: 'Agent',
          function: 'updateMergedAgencyIds',
          category: UICategory.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              producerId: this.producerId,
            },
          },
        },
        'Failed to get agency mergers from CDM.'
      );
    }
  }
}
